import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { formatDate } from "lopermut"
import { CompanyClientCell } from "modules/customers/components/CompanyClientCell"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CustomerCell } from "modules/shared/components/CustomerCell"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useConversationTicketsQuery } from "modules/support/hooks/useConversationTicketsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/support/list/route"
import { SupportConversationTicketsResponseDataSchema, SupportSortFields } from "schema"
import { z } from "zod"
import { TicketIntegrationIcon } from "../TicketIntegrationBadges"
import { TicketStatusDropdown } from "./TicketStatusDropdown"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof SupportConversationTicketsResponseDataSchema>[number]
>()

type TicketQueryReturn = ReturnType<typeof useConversationTicketsQuery>

type Props = {
  tickets: TicketQueryReturn["data"]
  isPending: TicketQueryReturn["isPending"]
  fields: typeof SupportSortFields
  onSort: (field: (typeof SupportSortFields)[number]) => void
  search: {
    field: (typeof SupportSortFields)[number]
    order: "asc" | "desc"
    supportTab?: z.infer<typeof SearchSchema>["supportTab"]
  }
}

export function ConversationTicketsTable({ tickets, isPending, fields, onSort, search }: Props) {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()

  const someTicketHasIntegration = useMemo(
    () => Boolean(tickets && tickets.some((t) => t.integrations.length > 0)),
    [tickets],
  )

  const columns = useMemo(
    () =>
      columnHelper.columns(
        [
          columnHelper.display({
            id: "unansweredCustomerReply",
            header: () => null,
            cell: ({ row }) => {
              if (!row.original.hasUnansweredCustomerReply) return null

              return (
                <Tooltip>
                  <TooltipTrigger>
                    <Pulse className="fill-unanswered-customer-reply" />
                  </TooltipTrigger>
                  <TooltipContent>{t("support.tickets.unansweredCustomerReply")}</TooltipContent>
                </Tooltip>
              )
            },
            meta: {
              className: "w-px whitespace-nowrap px-2",
            },
          }),
          someTicketHasIntegration &&
            columnHelper.display({
              id: "integrations",
              header: () => null,
              cell: ({ row }) => {
                if (row.original.integrations.length === 0) return

                return (
                  <span className="flex flex-row gap-1">
                    {row.original.integrations.map((integration) => (
                      <Tooltip key={`${integration.kind}-${integration.externalId}`}>
                        <TooltipTrigger>
                          <TicketIntegrationIcon kind={integration.kind} className="size-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {t("support.tickets.escalatedThrough", {
                            kind: integration.kind,
                            createdAt: formatDate(integration.createdAt, "Pp"),
                          })}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </span>
                )
              },
              meta: {
                className: "w-px whitespace-nowrap pl-1 pr-2",
              },
            }),
          !isAmphoraCompany &&
            columnHelper.accessor("customer", {
              header: t("support.tickets.table.customer"),
              cell: ({ row }) => {
                return <CustomerCell customer={row.original.customer} />
              },
            }),
          isAmphoraCompany &&
            columnHelper.display({
              id: "client",
              header: t("support.tickets.table.client"),
              cell: ({ row }) => <CompanyClientCell client={row.original.customer.client} />,
            }),
          isAmphoraCompany &&
            columnHelper.accessor("conversation.orderId", {
              header: t("support.tickets.table.orderId"),
              cell: ({ getValue }) => getValue() ?? "-",
            }),
          columnHelper.accessor("campaign", {
            header: t("support.tickets.table.campaign"),
            cell: ({ row }) => {
              const campaign = "campaign" in row.original ? row.original.campaign : undefined
              if (!campaign)
                return (
                  <span className="text-muted-foreground font-italic">
                    {t("support.tickets.table.noCampaign")}
                  </span>
                )

              return campaign.name ?? "-"
            },
          }),
          columnHelper.display({
            id: "humanReply",
            header: t("support.tickets.table.replyType"),
            cell: ({ row }) => (
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant="outline"
                    className={
                      row.original.hasHumanReply
                        ? "border-support/40 bg-support/10 text-support dark:border-support-secondary/40 dark:text-support-secondary"
                        : undefined
                    }
                  >
                    {row.original.hasHumanReply
                      ? t("support.tickets.humanReply")
                      : t("support.tickets.automated")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {t(
                    row.original.hasHumanReply
                      ? "support.tickets.humanReplyTooltip"
                      : "support.tickets.automatedTooltip",
                  )}
                </TooltipContent>
              </Tooltip>
            ),
            meta: {
              className: "px-2",
            },
          }),
          columnHelper.accessor("status", {
            header: t("support.tickets.table.status"),
            cell: (props) => (
              <TicketStatusDropdown ticket={props.row.original} className="py-1.5" size="sm" />
            ),
            meta: {
              colSpan: 1,
              noPadding: true,
            },
          }),
          columnHelper.accessor("createdAt", {
            header: t("support.tickets.table.createdAt"),
            cell: (props) => <DateDisplay date={props.getValue()} />,
            meta: {
              colSpan: 1,
              align: "right",
            },
          }),
        ].filter(Boolean),
      ),
    [isAmphoraCompany, someTicketHasIntegration, t],
  )

  const table = useTable({
    data: tickets ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || tickets === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (tickets.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("support.tickets.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/support/list/$customerId",
          params: { customerId: row.original.customer.id },
          search: {
            ...search,
            conversationId: Number(row.original.conversationId),
            tab: "support",
            supportTab: search.supportTab || "tickets",
            ...search,
          },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<
      Features,
      z.infer<typeof SupportConversationTicketsResponseDataSchema>[number],
      unknown
    >,
  ) {
    if (!fields.includes(header.id as (typeof SupportSortFields)[number])) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as (typeof SupportSortFields)[number]),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
